import { Dialog, Radio, SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid'
import { CSSProperties, useEffect, useMemo, useState } from 'react'

import KanseiDataGrid from 'components/copied/dataGrid'
import ModalLayout from 'components/layouts/modalLayout'
import { helper } from 'helper'
import FixedSecuritySettingModal from 'pages/securityField/settings/components/fixedSecuritySettingModal'
import SectionComponent from 'pages/securityField/settings/components/section'
import { PrimaryWorkPatternContent } from 'types/primaryWorkPattern'
import { FixedSecurity, RoleType } from 'types/securitiesSecurityFields'
import { Security } from 'types/security'
import { WorkPatternContent } from 'types/workPattern'
import { Icons } from 'variables'

interface Props {
  workPatterns: (WorkPatternContent & { id: string })[]
  securities: Security[]
  fixedSecurities: FixedSecurity[]
  onDelete: (ids: string[]) => void
  onAdd: (data: {
    securityId: string
    primaryWorks: PrimaryWorkPatternContent[]
  }) => void
  onUpdate: (id: string, data: PrimaryWorkPatternContent[]) => void
  onUpdateLeaderRole: (
    updateRelationIds: string[],
    toNoneRelationIds: string[],
  ) => void
}

const Tab = {
  ACTIVE: 0,
  INACTIVE: 1,
} as const
type TabKey = (typeof Tab)[keyof typeof Tab]

const sectionTitle = '固定隊員設定'
const fixedSecuritiesColumns: GridColDef[] = [
  {
    field: 'editButton',
    headerName: '',
    align: 'center',
    sortable: false,
    width: 50,
  },
  {
    field: 'leaderFlg',
    headerName: '班長',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    width: 50,
  },
  {
    field: 'fixedSecurity',
    headerName: '隊員',
    width: 120,
  },
  {
    field: 'primaryWorkPatternIds',
    headerName: '優先勤務パターン',
    flex: 1,
  },
]
export default function FixedSecuritySettingComponent(props: Props) {
  const [openSettingModal, setOpenSettingModal] = useState<
    | boolean
    | {
        id: string
        security: Security
        relationIds: Set<string>
      }
  >(false)
  const [checked, setChecked] = useState<string[]>([])
  const [tab, setTab] = useState<TabKey | null>(null)

  const selectableSecurityOptions = useMemo(() => {
    const fixedSecurityIds = new Set(
      props.fixedSecurities.map((fixedSecurity) => fixedSecurity.securityId),
    )
    return props.securities
      .filter((security) => !fixedSecurityIds.has(security.id))
      .map((security) => ({
        label: helper.formatter.nameFormat(security),
        value: security.id,
      }))
  }, [props.securities, props.fixedSecurities])

  const workPatternOptions = props.workPatterns.map((workPattern) => ({
    label: workPattern.name,
    value: workPattern.id,
  }))

  // securityId -> entity
  const securityMap = useMemo<Map<string, Security>>(() => {
    return new Map(props.securities.map((security) => [security.id, security]))
  }, [props.securities])

  // workPatternId -> entity
  const workPatternMap = useMemo<
    Map<string, WorkPatternContent & { id: string }>
  >(() => {
    return new Map(
      props.workPatterns.map((workPattern) => [workPattern.id, workPattern]),
    )
  }, [props.workPatterns])

  const columns = useMemo(() => {
    return fixedSecuritiesColumns.map((column) => {
      if (column.field === 'editButton') {
        return {
          ...column,
          renderCell: (params: any) => {
            return (
              <IconButton onClick={handleClickEditor(params.row.id)}>
                <Icons.editButton />
              </IconButton>
            )
          },
        }
      }
      if (column.field === 'leaderFlg') {
        return {
          ...column,
          renderCell: (params: any) => {
            return (
              <Radio
                onClick={(e) => {
                  e.stopPropagation()
                }}
                checked={params.value === RoleType.RoleLeader}
                onChange={handleSelectLeader(params.row.id)}
                disabled={isCurrent(Tab.INACTIVE)}
              />
            )
          },
        }
      }
      if (column.field === 'primaryWorkPatternIds') {
        return {
          ...column,
          renderCell: (params: any) => {
            const workPatternIds: string[] = Array.from(params.value)
            const workPatternNames: string[] = workPatternIds.map(
              (id: string) => {
                return workPatternMap.get(id)?.name ?? ''
              },
            )
            return workPatternNames.join('　')
          },
        }
      } else {
        return column
      }
    })
  }, [props.workPatterns, tab])

  const rows = useMemo(() => {
    return props.fixedSecurities.map((fixedSecurity) => {
      const security = securityMap.get(fixedSecurity.securityId)
      return {
        id: fixedSecurity.id, // relation id
        leaderFlg: fixedSecurity.roleType,
        fixedSecurity: security ? helper.formatter.nameFormat(security) : '',
        primaryWorkPatternIds: new Set(
          fixedSecurity.primaryWorks.map((work) => work.workPatternId),
        ),
      }
    })
  }, [props.securities, props.workPatterns, props.fixedSecurities])

  const isOpenModal = () => {
    return openSettingModal !== false
  }

  const getDefaultValue = () => {
    if (typeof openSettingModal === 'object') {
      return openSettingModal
    }
  }

  const isCurrent = (tabTarget: TabKey): boolean => {
    return tab === tabTarget
  }

  // 班長タブの初期値設定
  useEffect(() => {
    // 既存値が入っている場合はそれに従う
    if (tab != null) return

    // 班長が存在すればACTIVEタブにする
    let hasLeader = false
    props.fixedSecurities.forEach((s) => {
      if (s.roleType === RoleType.RoleLeader) {
        hasLeader = true
      }
    })

    setTab(hasLeader ? Tab.ACTIVE : Tab.INACTIVE)
  }, [props.fixedSecurities, tab])

  const handleCheckbox = (e: GridRowSelectionModel) => {
    setChecked(e as string[])
  }

  const handleAdd = () => {
    setOpenSettingModal(true)
  }

  const handleDelete = () => {
    props.onDelete(checked)
  }

  const handleModalCancel = () => {
    setOpenSettingModal(false)
  }

  const handleModalAdd = (data: {
    securityId: string
    primaryWorks: PrimaryWorkPatternContent[]
  }) => {
    props.onAdd(data)
    setOpenSettingModal(false)
  }

  const handleModalUpdate = (
    id: string,
    data: {
      primaryWorks: PrimaryWorkPatternContent[]
    },
  ) => {
    props.onUpdate(id, data.primaryWorks)
    setOpenSettingModal(false)
  }

  const handleClickEditor = (id: string) => (e: React.MouseEvent) => {
    e.stopPropagation()
    const fixedSecurity = props.fixedSecurities.find(
      (fixedSecurity) => fixedSecurity.id === id,
    )
    if (fixedSecurity == null) return

    const security = securityMap.get(fixedSecurity.securityId)
    if (security == null) return

    const relationIds = fixedSecurity.primaryWorks.map(
      (work) => work.workPatternId,
    )

    setOpenSettingModal({
      id: id,
      security: security,
      relationIds: new Set(relationIds),
    })
  }

  const handleChangeLeaderTab = (tab: TabKey) => () => {
    if (tab === Tab.INACTIVE) {
      // 全員を役無しに変更
      const toNoneRelationIds = props.fixedSecurities.map((v) => v.id)
      props.onUpdateLeaderRole([], toNoneRelationIds)
    }
    setTab(tab)
  }

  const handleSelectLeader = (relationId: string) => () => {
    const targetRelation = props.fixedSecurities.find(
      (relation) => relation.id === relationId,
    )

    if (targetRelation == null) return

    // 役が無い場合は班長に変更
    if (targetRelation.roleType === RoleType.RoleNone) {
      // 他の人は役無しに変更
      const toNoneRelationIds = props.fixedSecurities
        .map((v) => v.id)
        .filter((id) => id !== relationId)
      props.onUpdateLeaderRole([relationId], toNoneRelationIds)
    }
  }

  return (
    <>
      <SectionComponent
        title={sectionTitle}
        actions={
          <>
            <Button variant="outlined" onClick={handleAdd}>
              <Icons.addButton />
              追加
            </Button>
            <Button variant="outlined" onClick={handleDelete}>
              <Icons.deleteButton />
              削除
            </Button>
            <Box style={leaderContainerStyle}>
              班長
              <Box style={tabButtonContainerStyle}>
                <Button
                  style={tabButtonStyle}
                  variant={isCurrent(Tab.INACTIVE) ? 'contained' : 'outlined'}
                  onClick={handleChangeLeaderTab(Tab.INACTIVE)}
                >
                  無
                </Button>
                <Button
                  style={tabButtonStyle}
                  variant={isCurrent(Tab.ACTIVE) ? 'contained' : 'outlined'}
                  onClick={handleChangeLeaderTab(Tab.ACTIVE)}
                >
                  有
                </Button>
              </Box>
            </Box>
          </>
        }
      >
        <KanseiDataGrid
          sx={dataGridStyle}
          rows={rows as []}
          columns={columns}
          handleChange={handleCheckbox}
        />
      </SectionComponent>
      <Dialog open={isOpenModal()}>
        <ModalLayout title={sectionTitle}>
          <FixedSecuritySettingModal
            workPatternOptions={workPatternOptions}
            securityOptions={selectableSecurityOptions}
            onCancel={handleModalCancel}
            onAdd={handleModalAdd}
            onUpdate={handleModalUpdate}
            defaultValue={getDefaultValue()}
          />
        </ModalLayout>
      </Dialog>
    </>
  )
}

const dataGridStyle: SxProps = {
  '& .MuiDataGrid-main': {
    height: '100%',
  },
  '& .MuiDataGrid-virtualScroller': {
    maxHeight: '400px',
    overflowY: 'scroll',
  },
}

const leaderContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginLeft: '16px',
}

const tabButtonContainerStyle: CSSProperties = {
  borderRadius: '4px',
  overflow: 'hidden',
  border: '1px solid #ccc',
}

const tabButtonStyle: CSSProperties = {
  border: 'none',
  boxShadow: 'none',
  borderRadius: 0,
  height: '30px',
  aspectRatio: '1.0',
  minWidth: 'unset',
}
